import React, { useEffect, useCallback, useRef, useState } from "react";
import { GridCellState } from "../GridCellState";
import { ClueSpec } from "../ClueSpec";
import "./CrosswordGrid.css";
import { Mode } from "./CrosswordMode";
import axios from "axios";
import logger from "../utils/logger";

import { CrosswordSessionUpdateDto } from "../models/crossword-session-update-dto";
import { Atted } from "../utils/misc";
import { Clue } from "../models/clue";
import Fireworks from "./Fireworks";
import CrosswordCell from "./CrosswordCell"; // Import the new component
import { CluesInGroup } from "../CrosswordUtils";
import { ComparisonSessionDto } from "../models/comparison-session-dto";
import { generateDistinctColorOptimized } from "../utils/colors";

interface CrosswordGridProps {
  grid: GridCellState[][];
  mode: Mode;
  readonly: boolean;
  selectedClue: ClueSpec;
  onClueChange: (clueSpec: ClueSpec) => void;
  setGridState: React.Dispatch<React.SetStateAction<GridCellState[][]>>;
  sessionId: number;
  userKey: string;
  numberOfUsers: number;
  loading: boolean;
  clues: Clue[];
  scratchpad: string; // for passing in updates
  updatedCells: { row: number; col: number }[]; // Array of cells that were updated
  // New callback prop to expose puzzle completion status
  onPuzzleCompleteChange?: (isComplete: boolean) => void;
  startTime?: Date;
  completionTime?: Date;
  publicationDate?: Date;
}

const CrosswordGrid: React.FC<CrosswordGridProps> = ({
  grid,
  mode,
  readonly,
  selectedClue,
  onClueChange,
  setGridState,
  sessionId,
  userKey,
  numberOfUsers,
  loading,
  clues,
  scratchpad,
  updatedCells,
  onPuzzleCompleteChange,
  startTime,
  completionTime: completionTimeProp,
  publicationDate,
}) => {
  const [isPuzzleComplete, setIsPuzzleComplete] = useState<boolean>(false);
  const [solutionHasErrors, setSolutionHasErrors] = useState<boolean>(false);
  const [showFireworks, setShowFireworks] = useState<boolean>(false);
  const [haveShownFireworks, setHaveShownFireworks] = useState<boolean>(false);
  const [completionTime, setCompletionTime] = useState<Date | null>(null);
  const [comparisonSession, setComparisonSession] = useState<ComparisonSessionDto | null>(null);
  const [isJustCompleted, setIsJustCompleted] = useState<boolean>(false);

  const formatDurationSeconds = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  };

  const formatDuration = (start: Date, end: Date): string =>
    formatDurationSeconds(Math.round((end.getTime() - start.getTime()) / 1000));

  const isCellUpdating = (row: number, col: number) => {
    return updatedCells.some((cell) => cell.row === row && cell.col === col);
  };
  const selectedCell = React.useMemo(() => {
    return selectedClue.isSet()
      ? { row: selectedClue.selectedRow, col: selectedClue.selectedCol }
      : null;
  }, [selectedClue]);

  const checkIfPuzzleComplete = useCallback(
    (currentGrid: GridCellState[][]) => {
      let foundError = false;
      for (let row = 0; row < currentGrid.length; row++) {
        for (let col = 0; col < currentGrid[row].length; col++) {
          const currentCell = currentGrid[row][col];
          if (currentCell.isBlack()) continue;
          if (currentCell.userText === "") {
            setHaveShownFireworks(false);
            setIsPuzzleComplete(false);
            if (onPuzzleCompleteChange) onPuzzleCompleteChange(false);
            return false;
          }
          if (
            currentCell.solutionText &&
            currentCell.solutionText != "" &&
            currentCell.userText !== currentCell.solutionText
          ) {
            foundError = true;
          }
        }
      }
      if (solutionHasErrors && !foundError) {
        setHaveShownFireworks(false);
      }
      setSolutionHasErrors(foundError);
      setIsPuzzleComplete(true);

      // Only trigger fireworks when transitioning from incomplete to complete without errors
      if (!haveShownFireworks && !foundError) {
        setHaveShownFireworks(true);
        setShowFireworks(true);
        setIsJustCompleted(true);

        // Send API call to update the completion status and time
        if (sessionId > 0) {
          axios.post(`/api/cross/session/${sessionId}/complete`)
            .then(response => {
              if (response.data?.completionTime) {
                setCompletionTime(new Date(response.data.completionTime));
              }
            })
            .catch(error => {
              logger.error("Error marking session as complete:", error);
            });
        }
      }

      if (onPuzzleCompleteChange) onPuzzleCompleteChange(true);
      return true;
    },
    [haveShownFireworks, solutionHasErrors, onPuzzleCompleteChange, sessionId, setIsJustCompleted]
  );

  const handleCellChange = useCallback(
    (newGrid: GridCellState[][], row: number, col: number) => {
      const update: CrosswordSessionUpdateDto = {
        sessionId: sessionId,
        gridState: newGrid
          .map((row) =>
            row.map((cell) => Atted(cell.userText) + Atted(cell.enteringUserKey)).join("")
          )
          .join(""),
        rowChanged: row,
        colChanged: col,
        scratchpad: scratchpad,
        userCount: 0, // only used Server->Client
      };
      setTimeout(() => {
        checkIfPuzzleComplete(newGrid);
      }, 0);
      axios.patch<CrosswordSessionUpdateDto>(`/api/cross/session`, update).catch((error) => {
        logger.error("Error patching session:", error);
      });
    },
    [sessionId, checkIfPuzzleComplete, scratchpad]
  );

  const handleCellClick = useCallback(
    (rowIndex: number, colIndex: number, cell: GridCellState, automove: boolean) => {
      if (readonly) return;
      if (cell.isBlack()) {
        onClueChange(new ClueSpec());
        return;
      }
      if (!automove) {
        if (cell.clueNum.isSet()) {
          if (selectedClue.number === cell.clueNum.number) {
            const newClue = new ClueSpec();
            newClue.number = cell.clueNum.number;
            newClue.selectedRow = rowIndex;
            newClue.selectedCol = colIndex;
            if (selectedClue.direction === "both") {
              newClue.direction = "across";
              newClue.number = cell.containingAcrossClue?.clueNumber ?? 0;
            } else if (selectedClue.direction === "across") {
              newClue.direction = "down";
              newClue.number = cell.containingDownClue?.clueNumber ?? 0;
            } else if (selectedClue.direction === "down") {
              newClue.direction = "across";
              newClue.number = cell.containingAcrossClue?.clueNumber ?? 0;
            }
            onClueChange(newClue);
          } else {
            const newClue = new ClueSpec(
              cell.clueNum.number,
              cell.clueNum.direction,
              rowIndex,
              colIndex
            );
            onClueChange(newClue);
          }
          return;
        }
      }
      if (!cell.containingAcrossClue) {
        onClueChange(new ClueSpec(cell.containingDownClue?.clueNumber, "down", rowIndex, colIndex));
        return;
      }
      if (!cell.containingDownClue) {
        onClueChange(
          new ClueSpec(cell.containingAcrossClue.clueNumber, "across", rowIndex, colIndex)
        );
        return;
      }
      if (automove) {
        onClueChange(new ClueSpec(selectedClue.number, selectedClue.direction, rowIndex, colIndex));
        return;
      }
      if (selectedClue.direction === "both") {
        onClueChange(
          new ClueSpec(cell.containingAcrossClue?.clueNumber, "across", rowIndex, colIndex)
        );
      } else if (selectedClue.direction === "across") {
        onClueChange(new ClueSpec(cell.containingDownClue.clueNumber, "down", rowIndex, colIndex));
      } else {
        onClueChange(
          new ClueSpec(cell.containingAcrossClue.clueNumber, "across", rowIndex, colIndex)
        );
      }
    },
    [onClueChange, selectedClue, readonly]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent | KeyboardEvent) => {
      if (readonly) return;
      if (!selectedCell) return;

      if (e.key === "Escape") {
        onClueChange(new ClueSpec());
        return;
      }

      if (e.key === "Delete" || e.key === "Backspace" || e.key === " ") {
        setGridState((prevGrid) => {
          const newGrid = prevGrid.map((row) => row.slice());
          const refCopy = newGrid[selectedCell.row][selectedCell.col];
          const cellToUpdate: GridCellState = {
            ...refCopy,
            setContainingClueNum: refCopy.setContainingClueNum,
            isBlack: refCopy.isBlack,
            getText: refCopy.getText,
            getClass: refCopy.getClass,
            PersistenceText: refCopy.PersistenceText,
          };
          cellToUpdate.userText = "";
          cellToUpdate.enteringUserKey = "@";
          newGrid[selectedCell.row][selectedCell.col] = cellToUpdate;
          handleCellChange(newGrid, selectedCell.row, selectedCell.col);
          return newGrid;
        });
        if (e.key === " ") {
          moveToNextClueCell(
            selectedClue,
            selectedCell,
            grid,
            clues,
            handleCellClick,
            onClueChange
          );
        }
        return;
      }

      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
        const rowDelta = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
        const colDelta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
        const newRow = selectedCell.row + rowDelta;
        const newCol = selectedCell.col + colDelta;
        if (newRow >= 0 && newRow < grid.length && newCol >= 0 && newCol < grid[0].length) {
          setTimeout(() => {
            handleCellClick(newRow, newCol, grid[newRow][newCol], true);
          }, 0);
        }
        return;
      }

      if (
        e.key.length !== 1 ||
        !/^\p{L}$/u.test(e.key) ||
        (e as KeyboardEvent).ctrlKey ||
        (e as KeyboardEvent).altKey ||
        (e as KeyboardEvent).metaKey
      ) {
        return;
      }
      const key = e.key.toUpperCase();
      setGridState((prevGrid) => {
        const newGrid = prevGrid.map((row) => row.slice());
        const refCopy = newGrid[selectedCell.row][selectedCell.col];
        const cellToUpdate: GridCellState = {
          ...refCopy,
          setContainingClueNum: refCopy.setContainingClueNum,
          isBlack: refCopy.isBlack,
          getText: refCopy.getText,
          getClass: refCopy.getClass,
          PersistenceText: refCopy.PersistenceText,
        };
        if (cellToUpdate.userText !== key) {
          cellToUpdate.userText = key;
          cellToUpdate.enteringUserKey = userKey;
        }
        newGrid[selectedCell.row][selectedCell.col] = cellToUpdate;
        handleCellChange(newGrid, selectedCell.row, selectedCell.col);
        moveToNextClueCell(
          selectedClue,
          selectedCell,
          newGrid,
          clues,
          handleCellClick,
          onClueChange
        );
        return newGrid;
      });
    },
    [
      readonly,
      selectedCell,
      grid,
      selectedClue,
      userKey,
      handleCellChange,
      handleCellClick,
      onClueChange,
      setGridState,
      clues,
    ]
  );

  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown as EventListener);
    return () => {
      window.removeEventListener("keydown", handleKeyDown as EventListener);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (selectedCell && hiddenInputRef.current) {
      const selectedElem = document.querySelector(".selected-cell") as HTMLElement;
      if (selectedElem) {
        const cellRect = selectedElem.getBoundingClientRect();
        const container = document.querySelector(".crossword-grid-container") as HTMLElement;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const offsetTop = cellRect.top - containerRect.top;
          hiddenInputRef.current.style.top = `${offsetTop}px`;
          hiddenInputRef.current.style.left = `${cellRect.left - containerRect.left}px`;
          hiddenInputRef.current.style.width = `${cellRect.width/2}px`;
        }
        hiddenInputRef.current.focus();
      }
    }
  }, [selectedCell]);

  // Fetch comparison stats when puzzle is cleanly complete
  useEffect(() => {
    if (isPuzzleComplete && !solutionHasErrors && sessionId > 0) {
      axios.get<ComparisonSessionDto>(`/api/cross/session/${sessionId}/comparison`)
        .then(response => {
          if (response.status === 200) setComparisonSession(response.data);
        })
        .catch(error => logger.error("Error fetching comparison session:", error));
    }
  }, [isPuzzleComplete, solutionHasErrors, sessionId]);

  // Check puzzle completion when grid changes - triggers fireworks if complete
  useEffect(() => {
    if (grid && grid.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      checkIfPuzzleComplete(grid);
    }
  }, [grid, checkIfPuzzleComplete]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="crossword-grid-container">
      {showFireworks && (
        <Fireworks
          duration={3000}
          burstCount={5}
          colors={["#ffffff", "#e6e6fa", "#d8bfd8"]}
          onComplete={() => setShowFireworks(false)}
        />
      )}
      <input
        ref={hiddenInputRef}
        type="text"
        style={{
          position: "absolute",
          top: "-1000px",
          left: "-1000px",
          opacity: 0,
        }}
        maxLength={1}
        onKeyDown={handleKeyDown}
        onChange={(e) => {
          handleKeyDown(new KeyboardEvent("keydown", { key: e.target.value }));
          e.target.value = "";
        }}
      />
      {isPuzzleComplete && !solutionHasErrors && (() => {
        const effectiveCompletion = completionTimeProp ?? completionTime;
        const currentDurationSeconds = startTime && effectiveCompletion
          ? Math.round((new Date(effectiveCompletion).getTime() - new Date(startTime).getTime()) / 1000)
          : null;
        const isNewRecord = isJustCompleted
          && currentDurationSeconds !== null
          && comparisonSession !== null
          && currentDurationSeconds < comparisonSession.durationSeconds;
        const dayOfWeek = publicationDate ? new Date(publicationDate).toLocaleDateString(undefined, { weekday: "long" }) : "";
        const playerCount = comparisonSession?.participants.length ?? 0;
        return (
          <div className="completion-message floating-status">
            <div className="completion-header">
              {isNewRecord
                ? `New Fastest ${playerCount}-player ${dayOfWeek} time!`
                : "Puzzle Complete!"}
              {startTime && effectiveCompletion && isNewRecord && (
                <div className="completion-duration">({formatDuration(new Date(startTime), new Date(effectiveCompletion))})</div>
              )}
              {startTime && effectiveCompletion && !isNewRecord && (
                <span className="completion-duration"> ({formatDuration(new Date(startTime), new Date(effectiveCompletion))})</span>
              )}
            </div>
            {comparisonSession && (
              <div className="completion-comparison">
                <span className="comparison-label">
                  {isNewRecord
                    ? "Previous fastest:"
                    : `Best ${comparisonSession.participants.length}-player ${new Date(comparisonSession.publicationDate).toLocaleDateString(undefined, { weekday: "short" })}:`}
                </span>
                <div className="comparison-participants">
                  {comparisonSession.participants.map((p, i) => (
                    <div
                      key={i}
                      className="participant-circle"
                      style={{ backgroundColor: generateDistinctColorOptimized(p.userKey, comparisonSession.participants.length, true) }}
                      title={p.initials}
                    >
                      {p.initials}
                    </div>
                  ))}
                </div>
                <span className="comparison-detail">
                  {formatDurationSeconds(comparisonSession.durationSeconds)}
                  {" · "}{new Date(comparisonSession.completionDate).toLocaleDateString()}
                  {comparisonSession.setter && ` · ${comparisonSession.setter}`}
                </span>
              </div>
            )}
          </div>
        );
      })()}
      {isPuzzleComplete && solutionHasErrors && (
        <div className="error-message floating-status">Solution has errors!</div>
      )}
      <div
        className="crossword-grid"
        style={{
          gridTemplateColumns: `repeat(${grid.length}, 30px)`,
          gridTemplateRows: `repeat(${grid.length}, 30px)`,
        }}
      >
        {grid.flatMap((row, rowIndex) =>
          row.map((cell, colIndex) => (
            <CrosswordCell
              key={`${rowIndex}-${colIndex}`}
              cell={cell}
              rowIndex={rowIndex}
              colIndex={colIndex}
              selectedCell={selectedCell}
              selectedClue={selectedClue}
              mode={mode}
              clues={clues}
              onCellClick={handleCellClick}
              isPuzzleComplete={isPuzzleComplete && !solutionHasErrors}
              isUpdating={isCellUpdating(rowIndex, colIndex)}
              numberOfUsers={numberOfUsers}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default CrosswordGrid;
function moveToNextClueCell(
  selectedClue: ClueSpec,
  selectedCell: { row: number; col: number },
  newGrid: GridCellState[][],
  clues: Clue[],
  handleCellClick: (
    rowIndex: number,
    colIndex: number,
    cell: GridCellState,
    automove: boolean
  ) => void,
  onClueChange: (clueSpec: ClueSpec) => void
) {
  const nextRow = selectedClue.direction === "down" ? selectedCell.row + 1 : selectedCell.row;
  const nextCol = selectedClue.direction === "across" ? selectedCell.col + 1 : selectedCell.col;
  if (
    nextRow < newGrid.length &&
    nextCol < newGrid[0].length &&
    !newGrid[nextRow][nextCol].isBlack()
  ) {
    setTimeout(() => {
      handleCellClick(nextRow, nextCol, newGrid[nextRow][nextCol], true);
    }, 0);
    return;
  }
  const currentClue: Clue | null =
    clues.find(
      (clue) =>
        clue.clueNumber === selectedClue.number &&
        clue.isAcross === (selectedClue.direction === "across")
    ) ?? null;
  const groupClues: Clue[] = CluesInGroup(clues, currentClue);
  if (!currentClue) return;

  // Check if this is the last clue in the group
  const currentIndex = groupClues.findIndex(
    (clue) => clue.clueNumber === currentClue.clueNumber && clue.isAcross === currentClue.isAcross
  );

  // If this is the last clue in the group or we couldn't find the clue, return
  if (currentIndex === -1 || currentIndex === groupClues.length - 1) return;
  // move to first cell of next clue
  const nextClue = groupClues[currentIndex + 1];
  const newClueSpec = new ClueSpec(
    nextClue.clueNumber,
    nextClue.isAcross ? "across" : "down",
    nextClue.row,
    nextClue.column
  );
  setTimeout(() => {
    onClueChange(newClueSpec);
  }, 0);
}
